import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PlayerRanking, MatchWithPlayersAndClub } from "../../../shared/schema";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Star, ChevronDown, ChevronRight, User, Trophy } from "lucide-react";
import { getEloTier } from "@/lib/elo";
import { formatDistanceToNow } from "date-fns";
import { fr, enUS, es } from 'date-fns/locale';
import { useTranslation } from "react-i18next";
import { apiRequest } from "@/lib/queryClient";
import PlayerStatisticsCompact from "./player-statistics-compact";
import TerrainsView from "./terrains-view";
import TournoisView from "./tournois-view";
import TournamentHistoryCard from "./tournament-history-card";

function PlayerClubsRow({ playerId, onNavigateToClub }: { playerId: string; onNavigateToClub?: (clubName: string) => void }) {
  const { data: clubs } = useQuery<Array<{ id: string; name: string; isAdmin: boolean }>>({
    queryKey: [`/api/players/${playerId}/clubs`],
    staleTime: 60_000,
  });

  if (!clubs || clubs.length === 0) return null;

  return (
    <div className="flex items-start gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-border">
      <img src="/tennis-ball.png" alt="" className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 dark:invert" />
      <div className="flex flex-wrap gap-1">
        {clubs.map(club => (
          <button
            key={club.id}
            onClick={() => onNavigateToClub?.(club.name)}
            className="inline-flex items-center gap-0.5 text-[11px] bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full border border-blue-100 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
          >
            {club.name}
            {club.isAdmin && <span className="text-[9px] opacity-60 ml-0.5">★</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

interface PlayersTabProps {
  playerId?: string | null;
  onNavigateToClub?: (clubName: string) => void;
}

type MainTab = "joueurs" | "tournois" | "terrains";

export default function PlayersTab({ playerId, onNavigateToClub }: PlayersTabProps) {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [activeMainTab, setActiveMainTab] = useState<MainTab>("joueurs");
  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null);

  // View all states
  const [showAllFavorites, setShowAllFavorites] = useState(false);
  const [showAllPartners, setShowAllPartners] = useState(false);
  const [showAllOpponents, setShowAllOpponents] = useState(false);

  // French-only features gate
  const isFrench = i18n.language?.split('-')[0] === 'fr';

  // Search mode toggle
  const [searchMode, setSearchMode] = useState<'fft' | 'padelo'>('padelo');

  // Search pagination
  const [searchOffset, setSearchOffset] = useState(0);
  const [accumulatedSearchResults, setAccumulatedSearchResults] = useState<any[]>([]);
  const [accumulatedHasMore, setAccumulatedHasMore] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Reset French-only states when language switches away from French
  useEffect(() => {
    if (!isFrench) {
      if (activeMainTab === 'terrains' || activeMainTab === 'tournois') setActiveMainTab('joueurs');
      if (searchMode === 'fft') setSearchMode('padelo');
    }
  }, [isFrench]);


  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setSearchOffset(0);
      setAccumulatedSearchResults([]);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);



  const [followedPlayers, setFollowedPlayers] = useState<Set<string>>(() => {
    const followKey = playerId ? `followedPlayers_${playerId}` : "followedPlayers";
    const savedArray = JSON.parse(localStorage.getItem(followKey) || "[]") as string[];
    const saved = new Set<string>(savedArray);
    if (playerId && !saved.has(playerId)) {
      saved.add(playerId);
      localStorage.setItem(followKey, JSON.stringify(Array.from(saved)));
    }
    return saved;
  });
  const { toast } = useToast();

  const { data: players = [], isLoading: isLoadingLocal } = useQuery<PlayerRanking[]>({
    queryKey: ["/api/players/rankings", "all"],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/players/rankings?timeFilter=all`);
      return response.json();
    }
  });

  // Tutorial Auto-Expansion logic
  useEffect(() => {
    const handleTourStep = (e: any) => {
        const { index, type, tab } = e.detail;
        if (tab === 'players' && index === 2 && (type === 'step:before' || type === 'tour:start')) {
            // Expand the first favorite if it exists
            const firstFav = players.find(p => followedPlayers.has(p.id));
            if (firstFav) {
                setExpandedPlayerId(firstFav.id);
            }
        }
    };

    window.addEventListener('padelo-tour-step', handleTourStep);
    return () => window.removeEventListener('padelo-tour-step', handleTourStep);
  }, [players, followedPlayers]);

  const { data: matches = [] } = useQuery<MatchWithPlayersAndClub[]>({
    queryKey: ["/api/matches"],
    select: (data: MatchWithPlayersAndClub[]) =>
      data.filter(match => match.isCompleted && match.isConfirmed)
        .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
  });

  const { data: fftSearchResults, isLoading: isLoadingFft } = useQuery({
    queryKey: ["/api/players/search/fft", debouncedSearchQuery, searchOffset],
    queryFn: async () => {
      if (!debouncedSearchQuery || debouncedSearchQuery.length < 2) return { results: [], hasMore: false };
      const response = await apiRequest("GET", `/api/players/search/fft?q=${encodeURIComponent(debouncedSearchQuery)}&offset=${searchOffset}`);
      return response.json();
    },
    enabled: debouncedSearchQuery.length >= 2 && searchMode === 'fft',
  });

  useEffect(() => {
    if (fftSearchResults?.results) {
      if (searchOffset === 0) {
        setAccumulatedSearchResults(fftSearchResults.results);
      } else {
        setAccumulatedSearchResults(prev => [...prev, ...fftSearchResults.results]);
      }
      setAccumulatedHasMore(fftSearchResults.hasMore ?? false);
    }
  }, [fftSearchResults, searchOffset]);
 
  // Get backend mutual statistics
  const { data: partnersData = [] } = useQuery<any[]>({
    queryKey: [`/api/players/${playerId}/partners`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/players/${playerId}/partners`);
      return response.json();
    },
    enabled: !!playerId,
  });
 
  const { data: opponentsData = [] } = useQuery<any[]>({
    queryKey: [`/api/players/${playerId}/opponents`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/players/${playerId}/opponents`);
      return response.json();
    },
    enabled: !!playerId,
  });


  const toggleFollowPlayer = (playerIdToToggle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!playerIdToToggle) return; // For mock FFT results without local ID
    const newFollowedPlayers = new Set(followedPlayers);
    if (followedPlayers.has(playerIdToToggle)) {
      newFollowedPlayers.delete(playerIdToToggle);
      toast({ description: t('players.actions.unfollowed') });
    } else {
      newFollowedPlayers.add(playerIdToToggle);
      toast({ description: t('players.actions.followed') });
    }
    setFollowedPlayers(newFollowedPlayers);
    const followKey = playerId ? `followedPlayers_${playerId}` : "followedPlayers";
    localStorage.setItem(followKey, JSON.stringify(Array.from(newFollowedPlayers)));
  };

  const handlePlayerClick = (id: string) => {
    setExpandedPlayerId(prev => prev === id ? null : id);
  };

  const getTranslatedTier = (tierString: string) => {
    const match = tierString.match(/^(.+?) \((\d+)\)$/);
    if (match) {
      const [_, name, level] = match;
      return `${t(`ranking.tiers.${name.toLowerCase()}`)} (${level})`;
    }
    return t(`ranking.tiers.${tierString.toLowerCase()}`);
  };

  if (isLoadingLocal && !searchQuery) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-10 w-full mb-4" />
        <Skeleton className="h-10 w-full mb-4" />
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // Group local players
  const favoritesList = players.filter(p => followedPlayers.has(p.id));
 
  const partnersList = partnersData.map(pData => {
    const player = players.find(p => p.id === pData.partnerId);
    if (!player) return null;
    return {
      ...player,
      mutualStats: {
        matches: pData.matchesPlayed,
        winRate: pData.winRate
      }
    };
  }).filter(Boolean) as (PlayerRanking & { mutualStats: { matches: number, winRate: number } })[];
 
  const opponentsList = opponentsData.map(oData => {
    const player = players.find(p => p.id === oData.opponentId);
    if (!player) return null;
    return {
      ...player,
      mutualStats: {
        matches: oData.matchesPlayed,
        winRate: oData.winRate
      }
    };
  }).filter(Boolean) as (PlayerRanking & { mutualStats: { matches: number, winRate: number } })[];

  const renderPlayerCard = (player: any, renderType: 'favorite' | 'partner' | 'opponent', index: number) => {
    const isExpanded = expandedPlayerId === player.id;

    return (
      <div key={`${renderType}-${player.id}`} className="bg-white dark:bg-card rounded-lg shadow-sm border border-gray-100 dark:border-border overflow-hidden mb-2 transition-all">
        {/* Card Header (Always visible) */}
        <div
          className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-muted transition-colors"
          onClick={() => handlePlayerClick(player.id)}
        >
          {/* Rank Badge - Local players are registered by definition */}
          <div className="w-12 h-12 bg-[#e8eaf6] text-[#283593] font-bold flex flex-col items-center justify-center rounded flex-shrink-0 leading-none border-2 border-[#283593]">
             <span className="text-[10px] opacity-70 mb-0.5">#</span>
             <span className={String(player.rank || '-').length > 4 ? 'text-xs' : String(player.rank || '-').length > 3 ? 'text-sm' : 'text-base'}>
               {player.rank || '-'}
             </span>
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-foreground text-base truncate">{player.name}</div>
            <div className="text-xs text-muted-foreground mt-0.5 truncate">
               {renderType === 'favorite' && (
                 <>{player.eloRating} ELO {t('players.tab.level')}{getEloTier(player.eloRating, player.matchesPlayed).match(/\((\d+)\)/)?.[1] || '-'} • {(player as any).fftLicence ? (
                   <span
                     className="cursor-pointer hover:text-blue-500 transition-colors"
                     onClick={(e) => { e.stopPropagation(); const txt = `${player.name} ${(player as any).fftLicence}`; navigator.clipboard.writeText(txt); toast({ description: `Copié : ${txt}`, duration: 1500 }); }}
                   >FFT #{(player as any).fftLicence}</span>
                 ) : `FFT ${player.fftRank ? `#${player.fftRank}` : '(-)'}`}</>
               )}
                {renderType === 'partner' && (
                  <><span className="text-blue-600 dark-text-active font-medium">{player.mutualStats?.winRate || 0}{t('players.card.winRate')}</span> ({player.mutualStats?.matches || 0})</>
                )}
                {renderType === 'opponent' && (
                  <><span className="text-blue-600 dark-text-active font-medium">{player.mutualStats?.winRate || 0}{t('players.card.winRate')}</span> ({player.mutualStats?.matches || 0})</>
                )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button 
              onClick={(e) => toggleFollowPlayer(player.id, e)}
              className="p-1.5 rounded-full transition-colors"
            >
              <Star className={`w-5 h-5 ${followedPlayers.has(player.id) ? 'fill-blue-600 text-blue-600' : 'text-muted-foreground'}`} />
            </button>
            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="px-3 pb-3 pt-1 border-t border-gray-50 dark:border-border bg-gray-50/30 dark:bg-transparent">
            <div className="grid grid-cols-2 gap-3 mt-2">
                {/* FFT Block */}
                <div className="bg-gray-50 dark:bg-muted p-3 rounded-xl border border-gray-100 dark:border-border">
                    <div className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide mb-2">FFT</div>
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-xl font-bold text-foreground">
                            {player.fftRank ? `#${player.fftRank}` : <span className="text-muted-foreground">-</span>}
                        </span>
                        {player.fftEvolution != null && player.fftEvolution !== 0 && !isNaN(player.fftEvolution) && (
                            <span className={`text-xs font-semibold ${player.fftEvolution > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                {player.fftEvolution > 0 ? '▲' : '▼'}{Math.abs(player.fftEvolution)}
                            </span>
                        )}
                    </div>
                    {player.fftPoints != null && (
                        <div className="flex items-center gap-1 mt-1">
                            <span className="text-sm font-medium text-muted-foreground">{player.fftPoints} pts</span>
                        </div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-2 flex items-center gap-2 flex-wrap">
                        <span>{player.fftTournaments != null ? t('players.tab.played', { count: player.fftTournaments }) : '-'}</span>
                        <TournamentHistoryCard licence={(player as any).fftLicence ?? null} name={player.name} />
                </div>
                </div>
                {/* PadELO Block */}
                <div className="bg-gray-50 dark:bg-muted p-3 rounded-xl border border-gray-100 dark:border-border">
                    <div className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide mb-2">PADELO</div>
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-xl font-bold text-foreground">#{player.rank || '-'}</span>
                        {player.rankEvolution != null && player.rankEvolution !== 0 && (
                            <span className={`text-xs font-semibold ${player.rankEvolution > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                {player.rankEvolution > 0 ? '▲' : '▼'}{Math.abs(player.rankEvolution)}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                        <span className="text-sm font-medium text-blue-800">{player.eloRating} ELO</span>
                        {player.eloEvolution != null && player.eloEvolution !== 0 && (
                            <span className={`text-xs font-semibold ${player.eloEvolution > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                {player.eloEvolution > 0 ? '▲' : '▼'}{Math.abs(player.eloEvolution)}
                            </span>
                        )}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-2">
                        {t('players.tab.level')}{getEloTier(player.eloRating, player.matchesPlayed).match(/\((\d+)\)/)?.[1] || '-'} · {t('players.tab.played', { count: player.matchesPlayed })}
                    </div>
                </div>
            </div>
            {player.matchesPlayed > 0 && (
                <div className="mt-3">
                    <PlayerStatisticsCompact playerId={player.id} />
                </div>
            )}
            <PlayerClubsRow playerId={player.id} onNavigateToClub={onNavigateToClub} />
          </div>
        )}
      </div>
    );
  };

  const renderFftPlayerCard = (fftPlayer: any, index: number) => {
    // We use a pseudo ID for expand state if localId doesn't exist
    const pseudoId = fftPlayer.localId || `fft-${fftPlayer.nom}-${fftPlayer.prenom}`;
    const isExpanded = expandedPlayerId === pseudoId;
    
    const fullName = `${fftPlayer.prenom} ${fftPlayer.nom}`;
    // ELO Tier logic
    let tierText = '-';
    if (fftPlayer.localElo) {
        tierText = getEloTier(fftPlayer.localElo, fftPlayer.localMatchesPlayed || 0).match(/\((\d+)\)/)?.[1] || '-';
    }

    return (
      <div key={`fft-${index}`} className="bg-white dark:bg-card rounded-lg shadow-sm border border-gray-100 dark:border-border overflow-hidden mb-2 transition-all">
        {/* Card Header (Always visible) */}
        <div
          className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-muted transition-colors"
          onClick={() => handlePlayerClick(pseudoId)}
        >
          {/* Rank Badge */}
          <div className={`w-12 h-12 font-bold flex flex-col items-center justify-center rounded flex-shrink-0 leading-none ${fftPlayer.isRegistered ? 'bg-[#e8eaf6] dark:bg-blue-900/50 text-[#283593] dark:text-blue-300 border-2 border-[#283593] dark:border-blue-700' : 'bg-[#e8eaf6] text-[#283593]'}`}>
             <span className="text-[10px] opacity-70 mb-0.5">#</span>
             <span className={String(fftPlayer.rank || '-').length > 4 ? 'text-xs' : String(fftPlayer.rank || '-').length > 3 ? 'text-sm' : 'text-base'}>
               {fftPlayer.rank || '-'}
             </span>
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-foreground text-base truncate">{fullName}</div>
            <div className="text-xs text-muted-foreground mt-0.5 truncate">
               {fftPlayer.isRegistered ? (
                 <span className="font-medium text-blue-800">{fftPlayer.localElo} ELO {t('players.tab.level')}{tierText}</span>
               ) : (
                 <span className="italic">{t('players.tab.noPlayersRegistered')}</span>
               )}
               <span className="text-muted-foreground mx-1">•</span> 
               {fftPlayer.licence ? (
                 <span
                   className="cursor-pointer hover:text-blue-500 transition-colors"
                   onClick={(e) => { e.stopPropagation(); const txt = `${fullName} ${fftPlayer.licence}`; navigator.clipboard.writeText(txt); toast({ description: `Copié : ${txt}`, duration: 1500 }); }}
                 >FFT #{fftPlayer.licence}</span>
               ) : `FFT ${fftPlayer.rank ? `#${fftPlayer.rank}` : '(-)'}`}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {fftPlayer.localId && (
                <button 
                onClick={(e) => toggleFollowPlayer(fftPlayer.localId, e)}
                className="p-1.5 rounded-full transition-colors"
                >
                <Star className={`w-5 h-5 ${followedPlayers.has(fftPlayer.localId) ? 'fill-blue-600 text-blue-600' : 'text-muted-foreground'}`} />
                </button>
            )}
            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="px-3 pb-3 pt-1 border-t border-gray-50 dark:border-border bg-gray-50/30 dark:bg-transparent">
            <div className="grid grid-cols-2 gap-3 mt-2">
                {/* FFT Block */}
                <div className="bg-gray-50 dark:bg-muted p-3 rounded-xl border border-gray-100 dark:border-border">
                    <div className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide mb-2">FFT</div>
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-xl font-bold text-foreground">
                            {fftPlayer.rank ? `#${fftPlayer.rank}` : <span className="text-muted-foreground">-</span>}
                        </span>
                        {fftPlayer.evolution != null && fftPlayer.evolution !== 0 ? (
                            <span className={`text-xs font-semibold ${fftPlayer.evolution > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                {fftPlayer.evolution > 0 ? '▲' : '▼'}{Math.abs(fftPlayer.evolution)}
                            </span>
                        ) : fftPlayer.evolution === 0 ? (
                            <span className="text-xs text-muted-foreground">=</span>
                        ) : null}
                    </div>
                    {fftPlayer.points != null && (
                        <div className="flex items-center gap-1 mt-1">
                            <span className="text-sm font-medium text-muted-foreground">{fftPlayer.points} pts</span>
                        </div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-2 flex items-center gap-2 flex-wrap">
                        <span>{fftPlayer.tournaments != null ? t('players.tab.played', { count: fftPlayer.tournaments }) : '-'}</span>
                        <TournamentHistoryCard idCrm={fftPlayer.idCrm ?? null} licence={fftPlayer.licence ?? null} name={fullName} />
                    </div>
                </div>
                {/* PadELO Block */}
                <div className="bg-gray-50 dark:bg-muted p-3 rounded-xl border border-gray-100 dark:border-border">
                    <div className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide mb-2">PADELO</div>
                    {fftPlayer.isRegistered ? (
                        <>
                            <div className="flex items-baseline gap-1.5">
                                <span className="text-xl font-bold text-foreground">#{fftPlayer.localRank || '-'}</span>
                                {fftPlayer.localRankEvolution != null && fftPlayer.localRankEvolution !== 0 && (
                                    <span className={`text-xs font-semibold ${fftPlayer.localRankEvolution > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                        {fftPlayer.localRankEvolution > 0 ? '▲' : '▼'}{Math.abs(fftPlayer.localRankEvolution)}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                                <span className="text-sm font-medium text-blue-800">{fftPlayer.localElo} ELO</span>
                                {fftPlayer.localEloEvolution != null && fftPlayer.localEloEvolution !== 0 && (
                                    <span className={`text-xs font-semibold ${fftPlayer.localEloEvolution > 0 ? 'text-green-600' : 'text-red-500'}`}>
                                        {fftPlayer.localEloEvolution > 0 ? '▲' : '▼'}{Math.abs(fftPlayer.localEloEvolution)}
                                    </span>
                                )}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-2">
                                {t('players.tab.level')}{tierText} · {t('players.tab.played', { count: fftPlayer.localMatchesPlayed || 0 })}
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center h-full pt-2">
                            <span className="text-xs text-muted-foreground italic">{t('players.tab.noPlayersRegistered')}</span>
                        </div>
                    )}
                </div>
            </div>
            {fftPlayer.localId && fftPlayer.localMatchesPlayed > 0 && (
                <div className="mt-3">
                    <PlayerStatisticsCompact playerId={fftPlayer.localId} />
                </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSection = (title: string, data: PlayerRanking[], renderType: 'favorite' | 'partner' | 'opponent', showAllState: boolean, setShowAllState: (val: boolean) => void, isFirst: boolean = false) => {
    if (data.length === 0) return null; // Don't show empty sections
    
    const displayData = showAllState ? data : data.slice(0, 3);
    const hasMore = data.length > 3;

    return (
      <div className="mb-6">
         <div className="flex justify-between items-center mb-3">
             <h3 className="font-bold text-foreground text-lg">{title} <span className="text-sm font-normal text-muted-foreground">({data.length})</span></h3>
             {hasMore && (
                 <button 
                    onClick={() => setShowAllState(!showAllState)}
                    className="text-sm text-blue-800 dark-text-active font-medium hover:underline"
                 >
                     {showAllState ? t('players.tab.viewLess') : t('players.tab.viewAll')}
                 </button>
             )}
         </div>
         <div className="flex flex-col gap-0.5">
             {displayData.map((p, i) => {
               const card = renderPlayerCard(p, renderType, i);
               if (isFirst && i === 0) {
                 return <div key={`${renderType}-${p.id}`} id="tour-players-card">{card}</div>;
               }
               return card;
             })}
         </div>
      </div>
    );
  };

  return (
    <div data-testid="players-tab" className="bg-gray-50 dark:bg-muted min-h-full pb-20">
      <div className="bg-white dark:bg-card pt-4 px-4 shadow-sm border-b border-border">
        {/* Main Tabs */}
        <div className="flex space-x-6 text-sm font-medium text-muted-foreground overflow-x-auto hide-scrollbar">
          <button
            className={`pb-3 whitespace-nowrap border-b-2 transition-colors ${activeMainTab === 'joueurs' ? 'border-[#001489] text-[#001489] dark-text-active' : 'border-transparent hover:text-foreground'}`}
            onClick={() => setActiveMainTab('joueurs')}
          >
            {t('players.tab.players')}
          </button>
          {isFrench && (
            <button
              className={`pb-3 whitespace-nowrap border-b-2 transition-colors ${activeMainTab === 'tournois' ? 'border-[#001489] text-[#001489] dark-text-active' : 'border-transparent hover:text-foreground'}`}
              onClick={() => { setSearchQuery(''); setActiveMainTab('tournois'); }}
            >
              Tournois
            </button>
          )}
          {isFrench && (
            <button
              className={`pb-3 whitespace-nowrap border-b-2 transition-colors ${activeMainTab === 'terrains' ? 'border-[#001489] text-[#001489] dark-text-active' : 'border-transparent hover:text-foreground'}`}
              onClick={() => { setSearchQuery(''); setActiveMainTab('terrains'); }}
            >
              {t('players.tab.courts')}
            </button>
          )}
        </div>

        {/* Search - visible only in Joueurs tab */}
        {activeMainTab === 'joueurs' && (
          <div className="flex items-center gap-2 mt-3 mb-4" id="tour-players-search">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                type="text"
                placeholder={searchMode === 'fft' ? 'Rechercher dans la base FFT...' : 'Rechercher un joueur PadELO...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                className={`pl-10 ${searchQuery ? 'pr-8' : 'pr-3'} bg-gray-50 dark:bg-muted border-transparent focus-visible:ring-1 focus-visible:bg-white dark:focus-visible:bg-card text-xs placeholder:text-gray-400 dark:placeholder:text-muted-foreground h-9`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              )}
              {isSearchFocused && !searchQuery && searchMode === 'fft' && (
                <div className="absolute top-full left-0 mt-1 z-20 bg-padel-green text-white text-xs rounded-lg px-3 py-2 shadow-lg w-full">
                  <p className="text-white/80 mb-1">{t('players.tab.searchTooltipTitle')}</p>
                  <p className="font-bold">{t('players.tab.searchTooltipAnd')}</p>
                  <p className="font-bold mt-0.5">{t('players.tab.searchTooltipOr')}</p>
                </div>
              )}
            </div>
            {isFrench && (
              <div className="flex-shrink-0 flex bg-gray-200 dark:bg-muted rounded p-0.5 gap-0.5">
                <button
                  onClick={() => setSearchMode('padelo')}
                  className={`text-[10px] font-semibold px-2 py-1 rounded transition-colors ${searchMode === 'padelo' ? 'bg-white dark:bg-card text-[#001489] shadow-sm dark-btn-active' : 'text-gray-500 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-foreground'}`}
                >
                  PADELO
                </button>
                <button
                  onClick={() => setSearchMode('fft')}
                  className={`text-[10px] font-semibold px-2 py-1 rounded transition-colors ${searchMode === 'fft' ? 'bg-white dark:bg-card text-[#001489] shadow-sm dark-btn-active' : 'text-gray-500 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-foreground'}`}
                >
                  FFT
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {activeMainTab === 'tournois' && searchQuery.length === 0 ? (
        <TournoisView playerId={playerId} />
      ) : activeMainTab === 'terrains' && searchQuery.length === 0 ? (
        <TerrainsView />
      ) : (
        <div className="p-4" id="tour-players-filters">
           {searchQuery.length > 0 ? (
               <div>
                  {searchMode === 'padelo' ? (
                      (() => {
                          const normalize = (s: string) =>
                              s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
                          const q = normalize(searchQuery);
                          const localResults = players.filter(p =>
                              normalize(p.name).includes(q)
                          );
                          return (
                              <>
                                  <h3 className="font-bold text-foreground text-lg mb-3">
                                      {t('players.tab.searchTitle')} {localResults.length > 0 && <span className="text-sm font-normal text-muted-foreground">({localResults.length})</span>}
                                  </h3>
                                  {localResults.length > 0 ? (
                                      <div className="flex flex-col gap-0.5">
                                          {localResults.map((p, i) => renderPlayerCard(p, 'favorite', i))}
                                      </div>
                                  ) : (
                                      <div className="text-center py-10 text-muted-foreground">
                                          {t('players.tab.searchNoResults', { query: searchQuery })}
                                      </div>
                                  )}
                              </>
                          );
                      })()
                  ) : (
                      <>
                          <h3 className="font-bold text-foreground text-lg mb-3">{t('players.tab.searchTitle')} {accumulatedSearchResults.length > 0 && <span className="text-sm font-normal text-muted-foreground">({accumulatedSearchResults.length}{accumulatedHasMore ? '+' : ''})</span>}</h3>
                          {isLoadingFft && searchOffset === 0 ? (
                              <div className="space-y-3">
                              {Array.from({ length: 3 }).map((_, i) => (
                                  <Skeleton key={i} className="h-20 w-full rounded-lg" />
                              ))}
                              </div>
                          ) : accumulatedSearchResults.length > 0 ? (
                              <div className="flex flex-col gap-0.5">
                                  {accumulatedSearchResults.map((p: any, i: number) => renderFftPlayerCard(p, i))}
                                  {accumulatedHasMore && (
                                      <button
                                          onClick={() => setSearchOffset(prev => prev + 10)}
                                          disabled={isLoadingFft}
                                          className="mt-3 w-full py-2 text-sm text-blue-800 font-medium border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-50"
                                      >
                                          {isLoadingFft ? '...' : t('players.tab.searchShowMore')}
                                      </button>
                                  )}
                              </div>
                          ) : debouncedSearchQuery.length >= 2 ? (
                              <div className="text-center py-10 text-muted-foreground">
                                  {t('players.tab.searchNoResults', { query: searchQuery })}
                              </div>
                          ) : (
                              <div className="text-center py-10 text-muted-foreground text-sm">
                                  {t('players.tab.searchMinChars')}
                              </div>
                          )}
                      </>
                  )}
               </div>
           ) : (
               // Normal View
               <>
                  {activeMainTab === 'joueurs' && (
                      <>
                          {renderSection(t('players.tab.favorites'), favoritesList, 'favorite', showAllFavorites, setShowAllFavorites, true)}
                          {playerId && renderSection(t('players.tab.partners'), partnersList, 'partner', showAllPartners, setShowAllPartners)}
                          {playerId && renderSection(t('players.tab.opponents'), opponentsList, 'opponent', showAllOpponents, setShowAllOpponents)}

                          {favoritesList.length === 0 && partnersList.length === 0 && opponentsList.length === 0 && (
                              <div className="text-center py-10 text-muted-foreground">
                                  {t('players.tab.noFavorites')}
                              </div>
                          )}
                      </>
                  )}

                  {activeMainTab === 'tournois' && (
                      <div className="text-center py-10 text-muted-foreground flex flex-col items-center">
                          <Trophy className="w-12 h-12 mb-3 text-muted-foreground" />
                          <p className="font-medium text-foreground">{t('players.tab.tournaments')}</p>
                          <p className="text-sm">{t('players.tab.soon')}</p>
                      </div>
                  )}
               </>
           )}
        </div>
      )}
    </div>
  );
}
